import { useState, useCallback } from 'react';

/**
 * Custom hook to manage field-level format validation errors in EPM forms.
 * Provides functions to validate fields synchronously, show inline error messages,
 * and clear errors when the user corrects input.
 */
const useEpmValidation = () => {
    const [fieldErrors, setFieldErrors] = useState({});

    const setFieldError = useCallback((fieldKey, errorMessage) => {
        setFieldErrors((prev) => {
            if (prev[fieldKey] === errorMessage) return prev;
            return { ...prev, [fieldKey]: errorMessage };
        });
    }, []);

    const clearFieldError = useCallback((fieldKey) => {
        setFieldErrors((prev) => {
            if (!prev[fieldKey]) return prev;
            const next = { ...prev };
            delete next[fieldKey];
            return next;
        });
    }, []);

    const validateFieldValue = useCallback((fieldKey, value, validatorFn) => {
        if (typeof validatorFn !== 'function') return true;
        const error = validatorFn(value);
        if (error) {
            setFieldError(fieldKey, error);
            return false;
        } else {
            clearFieldError(fieldKey);
            return true;
        }
    }, [setFieldError, clearFieldError]);

    return {
        fieldErrors,
        setFieldError,
        clearFieldError,
        validateFieldValue,
        hasFieldErrors: Object.keys(fieldErrors).length > 0,
    };
};

export default useEpmValidation;

